import { supabase } from '@/integrations/supabase/client';
import * as Sentry from "@sentry/react";
import {
  ProxyUser, 
  CreateProxySessionRequest, 
  ProxySessionResponse, 
  ProxySession 
} from '@/types/proxy';

export class ProxyAPI {
  /**
   * Generate a secure session token
   */
  private static generateSessionToken(): string {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  }

  /**
   * Get current device info for audit logging
   */
  private static getDeviceInfo() {
    return {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      timestamp: new Date().toISOString(),
      url: window.location.href
    };
  }

  /**
   * Check if current user can proxy into other accounts
   */
  static async canProxyUsers(): Promise<boolean> {
    try {
      const { data, error } = await supabase.rpc('check_user_permission', {
        permission_name: 'proxy_users'
      });
      
      if (error) throw error;
      return data || false;
    } catch (error) {
      Sentry.captureException(error, {
        tags: { context: 'api_error', endpoint: 'proxy_api', operation: 'proxy', action: 'check_permissions' },
        extra: { message: 'Error checking proxy permissions' }
      });
      return false;
    }
  }

  /**
   * Search for users that can be proxied
   */
  static async searchUsers(query: string, limit: number = 10): Promise<ProxyUser[]> {
    console.log('🔍 Search called with query:', query);
    
    try {
      // Validate query length
      if (query.length < 2) {
        console.log('🔍 Query too short, returning empty array');
        return [];
      }

      // Use security definer function to bypass RLS
      console.log('🔍 Calling search_users_for_platform_admin RPC...');
      const { data: searchResults, error } = await supabase.rpc(
        'search_users_for_platform_admin' as any,
        {
          search_query: query,
          result_limit: limit
        }
      ) as { data: any[] | null; error: any };

      if (error) {
        console.error('🔍 RPC error:', error);
        Sentry.captureException(error, {
          tags: { context: 'api_error', endpoint: 'proxy_api', operation: 'proxy', action: 'rpc_search' },
          extra: { message: 'RPC search failed', query }
        });
        throw error;
      }

      console.log('🔍 RPC search results:', searchResults);
      console.log('🔍 Results count:', searchResults?.length || 0);

      // Map results to ProxyUser type
      const results: ProxyUser[] = (searchResults || [])
        .filter((user: any) => user.role !== null)
        .map((user: any) => ({
          id: user.user_id,
          email: user.business_email || '',
          legal_first_name: user.legal_first_name,
          legal_last_name: user.legal_last_name,
          full_name: `${user.legal_first_name || ''} ${user.legal_last_name || ''}`.trim(),
          role: user.role || 'guest',
          organization_id: user.organization_id || '',
          status: user.is_active ? 'active' as const : 'inactive' as const
        }));

      console.log('🔍 Mapped results:', results);
      return results;
      
    } catch (error) {
      console.error('🔍 Search error:', error);
      Sentry.captureException(error, {
        tags: { context: 'api_error', endpoint: 'proxy_api', operation: 'proxy', action: 'search' },
        extra: { message: 'Search function error', query }
      });
      throw new Error('Failed to search users');
    }
  }

  /**
   * Validate if current user can proxy into target user
   */
  static async canProxyUser(targetUserId: string): Promise<boolean> {
    try {
      const { data: currentUser } = await supabase.auth.getUser();
      if (!currentUser.user) return false;

      const { data, error } = await supabase.rpc('can_proxy_user', {
        admin_id: currentUser.user.id,
        target_id: targetUserId
      });

      if (error) throw error;
      return data || false;
    } catch (error) {
      Sentry.captureException(error, {
        tags: { context: 'api_error', endpoint: 'proxy_api', operation: 'proxy', action: 'validate_permission' },
        extra: { message: 'Error validating proxy permission', targetUserId }
      });
      return false;
    }
  }

  /**
   * Create a new proxy session
   */
  static async createProxySession(request: CreateProxySessionRequest): Promise<ProxySessionResponse> {
    try {
      const { data: currentUser } = await supabase.auth.getUser();
      if (!currentUser.user) {
        throw new Error('Not authenticated');
      }

      // Validate permissions first
      const canProxy = await this.canProxyUser(request.target_user_id);
      if (!canProxy) {
        throw new Error('Insufficient permissions to proxy this user');
      }

      const sessionToken = this.generateSessionToken();
      const durationMs = (request.duration_minutes || 25) * 60 * 1000;
      const expiresAt = new Date(Date.now() + durationMs);

      // Get target user info
      const { data: targetProfile, error: profileError } = await supabase
        .from('profiles')
        .select(`
          user_id,
          legal_first_name,
          legal_last_name,
          business_email,
          user_organization_roles!inner(role, organization_id, is_active)
        `)
        .eq('user_id', request.target_user_id)
        .eq('user_organization_roles.is_active', true)
        .single();

      if (profileError) throw profileError;

      const userRole = targetProfile.user_organization_roles?.[0] as any;
      const targetUser: ProxyUser = {
        id: targetProfile.user_id,
        email: targetProfile.business_email || '',
        legal_first_name: targetProfile.legal_first_name || '',
        legal_last_name: targetProfile.legal_last_name || '',
        full_name: `${targetProfile.legal_first_name || ''} ${targetProfile.legal_last_name || ''}`.trim(),
        role: userRole?.role || 'guest',
        organization_id: userRole?.organization_id || '',
        status: 'active'
      };

      // Create proxy session
      const { data: session, error } = await supabase
        .from('proxy_sessions')
        .insert({
          admin_user_id: currentUser.user.id,
          target_user_id: request.target_user_id,
          session_token: sessionToken,
          expires_at: expiresAt.toISOString(),
          metadata: {
            reason: request.reason || 'Administrative access',
            ip_address: 'client-side', // Will be updated by edge function if needed
            device_info: this.getDeviceInfo(),
            target_name: targetUser.full_name,
            target_email: targetUser.email
          }
        })
        .select()
        .single();

      if (error) throw error;

      // Log the proxy session start
      await this.logProxyAction(session.id, 'proxy_session_start', {
        target_user_id: request.target_user_id,
        target_email: targetUser.email,
        reason: request.reason
      });

      return {
        session: session as ProxySession,
        target_user: targetUser,
        success: true,
        message: `Proxy session started for ${targetUser.full_name || targetUser.email}`
      };
    } catch (error) {
      Sentry.captureException(error, {
        tags: { context: 'api_error', endpoint: 'proxy_api', operation: 'proxy', action: 'create_session' },
        extra: { message: 'Error creating proxy session' }
      });
      throw error;
    }
  }

  /**
   * End an active proxy session
   */
  static async endProxySession(sessionId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('proxy_sessions')
        .update({
          is_active: false,
          ended_at: new Date().toISOString()
        })
        .eq('id', sessionId)
        .eq('is_active', true);

      if (error) throw error;

      // Log the proxy session end
      await this.logProxyAction(sessionId, 'proxy_session_end', {
        ended_at: new Date().toISOString(),
        reason: 'Manual termination'
      });
    } catch (error) {
      Sentry.captureException(error, {
        tags: { context: 'api_error', endpoint: 'proxy_api', operation: 'proxy', action: 'end_session' },
        extra: { message: 'Error ending proxy session', sessionId }
      });
      throw error;
    }
  }

  /**
   * Validate an existing proxy session
   */
  static async validateProxySession(sessionToken: string): Promise<ProxySession | null> {
    try {
      const { data, error } = await supabase
        .from('proxy_sessions')
        .select('*')
        .eq('session_token', sessionToken)
        .eq('is_active', true)
        .gte('expires_at', new Date().toISOString())
        .single();

      if (error || !data) return null;
      return data as ProxySession;
    } catch (error) {
      Sentry.captureException(error, {
        tags: { context: 'api_error', endpoint: 'proxy_api', operation: 'proxy', action: 'validate_session' },
        extra: { message: 'Error validating proxy session' }
      });
      return null;
    }
  }

  /**
   * Get current user's permissions
   */
  static async getUserPermissions(userId?: string): Promise<string[]> {
    try {
      const { data: currentUser } = await supabase.auth.getUser();
      const targetUserId = userId || currentUser.user?.id;
      
      if (!targetUserId) return [];

      const { data, error } = await supabase.rpc('get_user_permissions', {
        user_id: targetUserId
      });

      if (error) throw error;
      return data || [];
    } catch (error) {
      Sentry.captureException(error, {
        tags: { context: 'api_error', endpoint: 'proxy_api', operation: 'proxy', action: 'get_permissions' },
        extra: { message: 'Error getting user permissions', userId }
      });
      return [];
    }
  }

  /**
   * Log proxy action for audit trail
   */
  static async logProxyAction(sessionId: string, action: string, details?: any): Promise<void> {
    try {
      const { data: currentUser } = await supabase.auth.getUser();
      if (!currentUser.user) return;

      // Use the existing profile activity logging system
      await supabase.rpc('log_profile_activity', {
        p_profile_user_id: currentUser.user.id,
        p_action_type: action,
        p_field_name: 'proxy_session',
        p_old_value: null,
        p_new_value: details ? JSON.stringify(details) : null,
        p_metadata: {
          proxy_session_id: sessionId,
          timestamp: new Date().toISOString(),
          device_info: this.getDeviceInfo(),
          ...details
        },
        p_is_sensitive: true
      });
    } catch (error) {
      Sentry.captureException(error, {
        tags: { context: 'api_error', endpoint: 'proxy_api', operation: 'proxy', action: 'log_action' },
        extra: { message: 'Error logging proxy action', sessionId }
      });
    }
  }

  /**
   * Get active proxy sessions (for admins)
   */
  static async getActiveProxySessions(): Promise<ProxySession[]> {
    try {
      const { data, error } = await supabase
        .from('proxy_sessions')
        .select('*')
        .eq('is_active', true)
        .gte('expires_at', new Date().toISOString())
        .order('started_at', { ascending: false });

      if (error) throw error;
      return (data || []) as ProxySession[];
    } catch (error) {
      Sentry.captureException(error, {
        tags: { context: 'api_error', endpoint: 'proxy_api', operation: 'proxy', action: 'get_sessions' },
        extra: { message: 'Error fetching active proxy sessions' }
      });
      return [];
    }
  }

  /**
   * Clean up expired sessions
   */
  static async cleanupExpiredSessions(): Promise<number> {
    try {
      const { data, error } = await supabase.rpc('cleanup_expired_proxy_sessions');
      if (error) throw error;
      return data || 0;
    } catch (error) {
      Sentry.captureException(error, {
        tags: { context: 'api_error', endpoint: 'proxy_api', operation: 'proxy', action: 'cleanup' },
        extra: { message: 'Error cleaning up expired sessions' }
      });
      return 0;
    }
  }

  // ============= COMPREHENSIVE AUDIT SYSTEM =============

  /**
   * Audit Profile Data - Examine actual profile data structure
   */
  static async auditProfileData(): Promise<void> {
    console.log('=== PROFILE DATA AUDIT ===');
    
    try {
      // Get actual profile records with ALL fields
      const { data: allProfiles, error } = await supabase
        .from('profiles')
        .select('*')
        .limit(10);
        
      console.log('Raw profile data:', allProfiles);
      console.log('Sample profile structure:', allProfiles?.[0]);
      
      // Check for NULL values in search fields
      allProfiles?.forEach((profile, index) => {
        console.log(`Profile ${index}:`, {
          user_id: profile.user_id,
          legal_first_name: profile.legal_first_name,
          legal_last_name: profile.legal_last_name,
          business_email: profile.business_email,
          firstName_isNull: profile.legal_first_name === null,
          lastName_isNull: profile.legal_last_name === null,
          email_isNull: profile.business_email === null
        });
      });
    } catch (error) {
      Sentry.captureException(error, {
        tags: { context: 'api_error', endpoint: 'proxy_api', operation: 'proxy', action: 'audit_profile' },
        extra: { message: 'Profile data audit failed' }
      });
    }
  }

  /**
   * Audit User Context and Organization Filtering
   */
  static async auditUserContext(): Promise<void> {
    console.log('=== USER CONTEXT AUDIT ===');
    
    try {
      // Check current user
      const { data: { user } } = await supabase.auth.getUser();
      console.log('Current user:', user);
      
      // Check user's organization roles
      const { data: userRoles } = await supabase
        .from('user_organization_roles')
        .select('*')
        .eq('user_id', user?.id);
      console.log('User roles:', userRoles);
      
      // Check if search should be organization-filtered
      const userOrgId = userRoles?.[0]?.organization_id;
      if (userOrgId) {
        console.log('Testing organization-filtered search...');
        const { data: orgProfiles } = await supabase
          .from('profiles')
          .select('user_id, legal_first_name, legal_last_name, business_email')
          .limit(10);
        console.log('Profiles accessible to user:', orgProfiles);
      }
    } catch (error) {
      Sentry.captureException(error, {
        tags: { context: 'api_error', endpoint: 'proxy_api', operation: 'proxy', action: 'audit_context' },
        extra: { message: 'User context audit failed' }
      });
    }
  }

  /**
   * Audit Data Quality - Check for data quality issues
   */
  static async auditDataQuality(): Promise<void> {
    console.log('=== DATA QUALITY AUDIT ===');
    
    try {
      // Check for empty/null values
      const { data: nullCheck } = await supabase
        .from('profiles')
        .select('user_id, legal_first_name, legal_last_name, business_email')
        .or('legal_first_name.is.null,legal_last_name.is.null,business_email.is.null');
      console.log('Profiles with NULL search fields:', nullCheck);
      
      // Check for whitespace/encoding issues
      const { data: allData } = await supabase
        .from('profiles')
        .select('user_id, legal_first_name, legal_last_name, business_email')
        .limit(5);
        
      allData?.forEach(profile => {
        console.log('Profile data analysis:', {
          user_id: profile.user_id,
          firstName_length: profile.legal_first_name?.length,
          firstName_trimmed: profile.legal_first_name?.trim(),
          lastName_length: profile.legal_last_name?.length,
          lastName_trimmed: profile.legal_last_name?.trim(),
          email_length: profile.business_email?.length,
          email_trimmed: profile.business_email?.trim(),
          hasWhitespace: {
            firstName: profile.legal_first_name !== profile.legal_first_name?.trim(),
            lastName: profile.legal_last_name !== profile.legal_last_name?.trim(),
            email: profile.business_email !== profile.business_email?.trim()
          }
        });
      });
    } catch (error) {
      Sentry.captureException(error, {
        tags: { context: 'api_error', endpoint: 'proxy_api', operation: 'proxy', action: 'audit_quality' },
        extra: { message: 'Data quality audit failed' }
      });
    }
  }

  /**
   * Audit Search Components - Test each search component separately
   */
  static async auditSearchComponents(query: string): Promise<void> {
    console.log('=== SEARCH COMPONENT AUDIT ===');
    console.log('Testing query:', query);
    
    try {
      // Test 1: Email search only
      console.log('1. Testing email search...');
      const { data: emailResults, error: emailError } = await supabase
        .from('profiles')
        .select('user_id, legal_first_name, legal_last_name, business_email')
        .ilike('business_email', `%${query}%`);
      console.log('Email results:', emailResults, 'Error:', emailError);
      
      // Test 2: First name search only
      console.log('2. Testing first name search...');
      const { data: firstNameResults, error: firstNameError } = await supabase
        .from('profiles')
        .select('user_id, legal_first_name, legal_last_name, business_email')
        .ilike('legal_first_name', `%${query}%`);
      console.log('First name results:', firstNameResults, 'Error:', firstNameError);
      
      // Test 3: Last name search only
      console.log('3. Testing last name search...');
      const { data: lastNameResults, error: lastNameError } = await supabase
        .from('profiles')
        .select('user_id, legal_first_name, legal_last_name, business_email')
        .ilike('legal_last_name', `%${query}%`);
      console.log('Last name results:', lastNameResults, 'Error:', lastNameError);
      
      // Test 4: OR combination
      console.log('4. Testing OR combination...');
      const { data: orResults, error: orError } = await supabase
        .from('profiles')
        .select('user_id, legal_first_name, legal_last_name, business_email')
        .or(`business_email.ilike.%${query}%,legal_first_name.ilike.%${query}%,legal_last_name.ilike.%${query}%`);
      console.log('OR results:', orResults, 'Error:', orError);

      // Test 5: Exact field matching
      console.log('5. Testing exact matches...');
      const { data: exactTest } = await supabase
        .from('profiles')
        .select('user_id, legal_first_name, legal_last_name, business_email')
        .eq('legal_first_name', query);
      console.log('Exact first name match test:', exactTest);

      // Test 6: Case sensitivity
      console.log('6. Testing case sensitivity...');
      const { data: caseTest } = await supabase
        .from('profiles')
        .select('user_id, legal_first_name, legal_last_name, business_email')
        .ilike('legal_first_name', `%${query.toUpperCase()}%`);
      console.log('Case sensitivity test (uppercase):', caseTest);
    } catch (error) {
      Sentry.captureException(error, {
        tags: { context: 'api_error', endpoint: 'proxy_api', operation: 'proxy', action: 'audit_search' },
        extra: { message: 'Search component audit failed', query }
      });
    }
  }

  /**
   * Replicate Current Query - Test exact current search query logic
   */
  static async replicateCurrentQuery(query: string): Promise<void> {
    console.log('=== CURRENT QUERY REPLICATION ===');
    
    try {
      const queryParts = query.split(' ');
      const firstName = queryParts[0];
      const lastName = queryParts.slice(1).join(' ');
      
      console.log('Query parts:', { firstName, lastName });
      
      // Build search conditions exactly as in current implementation
      let searchConditions = [
        `business_email.ilike.%${query}%`,
        `legal_first_name.ilike.%${query}%`,
        `legal_last_name.ilike.%${query}%`
      ];

      if (lastName) {
        searchConditions.push(`legal_first_name.ilike.%${firstName}%`);
        searchConditions.push(`legal_last_name.ilike.%${lastName}%`);
      }

      const queryString = searchConditions.join(',');
      console.log('Generated query string:', queryString);
      
      // This is the EXACT query from the current implementation
      const { data, error } = await supabase
        .from('profiles')
        .select('user_id, legal_first_name, legal_last_name, business_email')
        .or(queryString);
        
      console.log('Current query results:', data);
      console.log('Current query error:', error);
      console.log('Query conditions used:', searchConditions);
    } catch (error) {
      Sentry.captureException(error, {
        tags: { context: 'api_error', endpoint: 'proxy_api', operation: 'proxy', action: 'audit_replication' },
        extra: { message: 'Query replication failed', query }
      });
    }
  }
}